import React from 'react';
import useStore from '../../store';

// UI Components
import MafiaChat from './chat/MafiaChat';
import MafiaActionScreen from './action/MafiaActionScreen';
import MafiaCountdown from './helpers/MafiaCountdown';
import MafiaTopBar from './hud/MafiaTopBar';
import MafiaThemeSelector from './screens/MafiaThemeSelector';
import MiniGameRouter from '../arcade/MiniGameRouter';
import GameIntro from '../arcade/ui/GameIntro';
import HintSystem from '../hints/HintSystem';

// Screens Groups
import * as System from './screens/MafiaSystemScreens';
import * as Blocker from './screens/MafiaBlockerScreens';
import * as Event from './screens/MafiaEventScreens';

// Hooks & Helpers
import { useMafiaTimer } from './hooks/useMafiaTimer';
import { useMafiaMinigame } from './hooks/useMafiaMinigame';
import { getUiState } from './helpers/mafiaUiState'; // <--- ИМПОРТ ЛОГИКИ

import './MafiaOverlay.css';


const MafiaOverlay = () => {
  useStore(state => state.render);
  const store = useStore.getState();
  const { info, mafia, telegram, work, menu } = store.ref;
  const game = info.game;
  const myId = telegram.telegramUserId;
  const { activeTab } = mafia;

  // --- 1. Подключаем Хуки ---

  // Логика времени и блокировки меню действий
  const {
    timeLeft,
    isMenuLocked,
    countdownValue,
    isCountdownDanger
  } = useMafiaTimer(game, mafia, activeTab);

  // Логика мини-игры
  const {
    showMiniGameIntro
  } = useMafiaMinigame(game, mafia);

  // Вычисляемые флаги
  const isRole = !!mafia.role;
  const amIDead = isRole && !info.game?.mafiaPublic?.alive?.[myId];
  const isClassicMafia = game?.subMode === 'mafia';
  const spectatorAccess = info.game?.mafiaPublic?.spectatorAccess;

  const isGhost = amIDead || (!isRole && isClassicMafia && spectatorAccess?.allowed);

  const hasActiveMinigame = !!mafia.minigameType;

  // === ПОЛУЧЕНИЕ СОСТОЯНИЯ UI ===
  const uiState = getUiState({
      game, mafia, isRole, isGhost, spectatorAccess, hasActiveMinigame,
      roleSeen: mafia.roleSeen,
      interrogationWelcomeSeen: mafia.interrogationWelcomeSeen,
      menu: menu,
  });

  const contentStyle = { marginTop: '100px', height: 'calc(100vh - 100px)', position: 'relative' };

  // Интро мини-игры имеет высокий приоритет, но ниже системных ошибок
  if (showMiniGameIntro && uiState.mode !== 'SYSTEM' && uiState.mode !== 'THEME_SELECT' && uiState.mode !== 'LOADING') {
      return <GameIntro gameType={mafia.minigameType} />;
  }

  // === РЕНДЕР ===
  return (
    <>
      <HintSystem />
      {(() => {
        switch (uiState.mode) {
          case 'LOADING':
              // Новый красивый экран загрузки
              return null;

      case 'THEME_SELECT':
          return <MafiaThemeSelector />;

      case 'SYSTEM':
          if (uiState.type === 'NOT_FOUND') return <System.NotFoundScreen isError={false} />;
          if (uiState.type === 'GAME_OVER') return <System.GameOverScreen />;
          if (uiState.type === 'ACCESS_DENIED') return <System.AccessDeniedScreen reason={spectatorAccess?.reason} />;
          if (uiState.type === 'SPECTATOR_WAIT') return <System.SpectatorScreen />;
          return null; // Fallback

      case 'BLOCKER':
          return (
            <>
              <MafiaCountdown value={countdownValue} isDanger={isCountdownDanger} />
              {uiState.type === 'DISCUSSION' && <Blocker.DiscussionPhaseScreen revealedWord={mafia.revealedWord} />}
              {uiState.type === 'VOTING' && <Blocker.VotingPhaseScreen />}
              {uiState.type === 'MAFIA_WAITING' && <Blocker.WaitTurnScreen isMafiaWaiting={true} />}
              {uiState.type === 'CIVILIAN_WAITING' && <Blocker.WaitTurnScreen isMafiaWaiting={false} />}
              {uiState.type === 'NIGHT_SLEEP' && <Blocker.NightSleepScreen />}

              <MafiaTopBar timeLeft={timeLeft} />
              <div className="mafia-content-area" style={contentStyle} />
            </>
          );

      case 'EVENT':
          return (
            <>
              <MafiaCountdown value={countdownValue} isDanger={isCountdownDanger} />
              {uiState.type === 'GHOST_WELCOME' && (
                  <Event.GhostWelcomeScreen
                      isSpectator={(!isRole && isClassicMafia && spectatorAccess?.allowed)}
                      onEnter={() => {
                          work.methods.hostAction('mafiaSpectatorJoin');
                          mafia.methods.closeRolePopup();
                          mafia.methods.setViewMode('chat_ghosts');
                      }}
                  />
              )}
              {uiState.type === 'ROLE_REVEAL' && (
                  <Event.RoleRevealScreen
                      role={mafia.role} team={mafia.team} word={mafia.word}
                      teammates={mafia.teammates} onClose={mafia.methods.closeRolePopup}
                      // ДОБАВИТЬ ЭТУ СТРОКУ:
                      definition={mafia.definition}
                  />
              )}
              {uiState.type === 'INTERROGATION' && (
                  <Event.InterrogationScreen
                      role={mafia.role} onDismiss={mafia.methods.dismissInterrogationWelcome}
                  />
              )}

              <MafiaTopBar timeLeft={timeLeft} />
              <div className="mafia-content-area" style={contentStyle}>
                 {/* Чат на фоне событий (например, для допроса) */}
                 {activeTab && activeTab.startsWith('chat_') && (
                    <MafiaChat room={activeTab.replace('chat_', '')} isGhost={isGhost} />
                 )}
              </div>
            </>
          );

      case 'GAMEPLAY':
      default:
          return (
            <>
              <MafiaCountdown value={countdownValue} isDanger={isCountdownDanger} />
              <MafiaTopBar timeLeft={timeLeft} />
              <div className="mafia-content-area" style={contentStyle}>
                  {activeTab === 'action' && <MafiaActionScreen isLocked={isMenuLocked} />}
                  {activeTab && activeTab.startsWith('chat_') && (
                      <MafiaChat
                          room={activeTab.replace('chat_', '')}
                          isGhost={isGhost}
                          onOpenShotMenu={() => mafia.methods.setActiveTab('action')}
                      />
                  )}
                  {activeTab === 'minigame' && (
                       <MiniGameRouter
                          gameType={mafia.minigameType || 'safeCracker'}
                          timeLeft={timeLeft}
                          onClose={() => window.Telegram?.WebApp?.close()}
                       />
                  )}
              </div>
            </>
          );
        }
      })()}
    </>
  );
};

export default MafiaOverlay;
